import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// -----------------------------
// Auth APIs
// -----------------------------
export const loginUser = async (username: string, password: string) => {
  const response = await axios.post(`${API_URL}/auth/login`, { username, password });
  return response.data;
};

export const registerUser = async (userData: any) => {
  const response = await axios.post(`${API_URL}/auth/register`, userData);
  return response.data;
};

// -----------------------------
// Vehicle APIs
// -----------------------------
export interface VehicleData {
  id: number;
  vehicleNo: string;
  ownerName: string;
  ownerPhone: string;
  vehicleType: string;
  monthlyFee: number;
  status: string;
  balance?: number;
}

export const registerVehicle = async (vehicle: VehicleData) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found. User not logged in.");
  const response = await axios.post(`${API_URL}/vehicles/createVehicle`, vehicle, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return response.data;
};

export const getVehicles = async () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found. User not logged in.");
  const response = await axios.get(`${API_URL}/vehicles`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getVehicleById = async (id: string) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found. User not logged in.");
  const response = await axios.get(`${API_URL}/vehicles/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateVehicle = async (id: string, vehicle: Partial<VehicleData>) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found. User not logged in.");
  const response = await axios.put(`${API_URL}/vehicles/${id}`, vehicle, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return response.data;
};

// -----------------------------
// Invoice APIs
// -----------------------------
export interface InvoiceData {
  vehicleId: number;
  description: string;
  amount: number;
}

// Get all invoices
export const getInvoices = async () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found. User not logged in.");
  const response = await axios.get(`${API_URL}/invoices`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Get invoices by vehicle
export const getInvoicesByVehicle = async (vehicleId: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/transactions/invoices/vehicle/${vehicleId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Get invoices by user
export const getInvoicesByUser = async (userId: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/transactions/invoices/user/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Create invoice
export const createInvoice = async (invoiceData: InvoiceData) => {
  const token = localStorage.getItem("token");
  const response = await axios.post(`${API_URL}/invoices`, invoiceData, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return response.data;
};

// Update invoice status
export const updateInvoiceStatus = async (id: string, status: string) => {
  const token = localStorage.getItem("token");
  const response = await axios.patch(`${API_URL}/invoices/${id}/status`, { status }, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return response.data;
};

// -----------------------------
// Payment APIs
// -----------------------------
export interface PaymentData {
  vehicleId: number;
  amount: number;
  paymentMode?: string;
  notes?: string;
}

// Payments by vehicle
export const getPaymentsByVehicle = async (vehicleId: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/transactions/payments/vehicle/${vehicleId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Payments by user
export const getPaymentsByUser = async (userId: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/transactions/payments/user/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Create payment
export const createPayment = async (paymentData: PaymentData) => {
  const token = localStorage.getItem("token");
  const response = await axios.post(`${API_URL}/payments`, paymentData, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return response.data;
};

// -----------------------------
// Write-Off APIs
// -----------------------------
export const getWriteOffsByVehicle = async (vehicleId: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/transactions/writeoffs/vehicle/${vehicleId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getWriteOffsByUser = async (userId: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/transactions/writeoffs/user/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const createWriteOff = async (data: { vehicleId: number; amount: number; reason: string }) => {
  const token = localStorage.getItem("token");
  const response = await axios.post(`${API_URL}/writeoffs`, data, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return response.data;
};

// -----------------------------
// Transactions APIs
// -----------------------------
export const getTransactionsByVehicle = async (vehicleId: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/transactions/vehicle/${vehicleId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getTransactionsByUser = async (userId: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/transactions/user/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// -----------------------------
// Report APIs
// -----------------------------

// Total number of vehicles
export const getTotalVehicles = async () => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/reports/vehicles`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Total outstanding balance
export const getTotalOutstanding = async () => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/reports/outstanding`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Monthly invoices
export const getMonthlyInvoices = async (month: number, year: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/reports/invoices?month=${month}&year=${year}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Monthly payments
export const getMonthlyPayments = async (month: number, year: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/reports/payments?month=${month}&year=${year}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Monthly write-offs
export const getMonthlyWriteOffs = async (month: number, year: number) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${API_URL}/reports/writeoffs?month=${month}&year=${year}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};
